import { useQuery } from "@tanstack/react-query";
import { MessageSquareText } from "lucide-react";

import { api } from "../api/client";
import type { SavedReply, TicketDetail } from "../api/types";
import { firstName } from "../lib/format";
import { Select } from "../ui/Select";

/** Words a saved reply can include, filled in for the ticket at hand. */
export const PLACEHOLDERS = [
  { token: "{requester}", describe: "the requester's full name" },
  { token: "{first_name}", describe: "the requester's first name" },
  { token: "{reference}", describe: "the ticket reference" },
  { token: "{my_name}", describe: "your name" },
] as const;

export function fillPlaceholders(body: string, ticket: TicketDetail, myName: string): string {
  const values: Record<string, string> = {
    "{requester}": ticket.requester.name,
    "{first_name}": firstName(ticket.requester.name),
    "{reference}": ticket.reference,
    "{my_name}": myName,
  };
  return body.replace(/\{(requester|first_name|reference|my_name)\}/g, (token) => values[token] ?? token);
}

export function useSavedReplies(enabled: boolean) {
  return useQuery({
    queryKey: ["saved-replies"],
    queryFn: ({ signal }) => api<SavedReply[]>("/api/saved-replies", { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** A menu of saved replies; choosing one inserts it, with placeholders filled in. */
export function SavedReplyPicker({
  ticket,
  myName,
  onInsert,
}: {
  ticket: TicketDetail;
  myName: string;
  onInsert: (text: string) => void;
}) {
  const replies = useSavedReplies(true);
  if (!replies.data?.length) return null;
  return (
    <Select
      label="Insert a saved reply"
      className="saved-reply-picker"
      size="sm"
      value=""
      placeholder="Saved replies"
      icon={<MessageSquareText size={16} aria-hidden="true" />}
      onChange={(id) => {
        const reply = replies.data.find((r) => r.id === id);
        if (reply) onInsert(fillPlaceholders(reply.body, ticket, myName));
      }}
      groups={[
        {
          options: replies.data.map((reply) => ({
            value: reply.id,
            label: reply.title,
            detail: reply.group_name ?? undefined,
          })),
        },
      ]}
    />
  );
}
